import { useChainId } from 'wagmi';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { memo } from 'react';
import { priceToClosestTick, tickToPrice } from '@uniswap/v4-sdk';
import { TickMath, nearestUsableTick } from '@uniswap/v3-sdk';
import { getPools } from '@/onchain/pools';
import { usePositionDetails } from '@/onchain/hooks/usePositionDetails';
import { usePoolDetails } from '@/onchain/hooks/usePoolDetails';
import { CurrencyField } from '@/types';
import { useCalculateLiquidity } from '@/onchain/hooks/useLiquidity';
import { parseAssetAmountInMinUnit } from '@/onchain/assets';
import { getMaxMinTick } from '@/utils/tickMath';
import { useAccountBalances } from '@/onchain/hooks/useAccountBalance';
import {
  convertNumberToBigIntByDecimals,
  isPositiveNumericInputValue,
  tryParsePrice,
} from '@/components/AddLiquidity/utils';
import { Bound } from '@/utils/types';
import { useLatestPositionId } from '@/onchain/hooks/useLatestPositionId';
import { usePoolPriceHistory } from '@/onchain/hooks/usePoolPriceHistory';
import { ADD_LIQUIDITY_SETTINGS_KEYS } from '@/components/common/Settings/settingKeys';
import { useGlobalSettings } from '@/components/common/Settings/GlobalSettingsProvider';
import { TxDeadlineSettingValue } from '@/components/common/Settings/TxDeadlineSetting';
import { DEFAULT_SETTINGS } from '@/components/common/Settings/defaultSettings';
import { isToken0PreferredQuoteToken } from '@/utils/tokenPreference';
import { getPositionManagerForHook } from '@/onchain/utils/hookConfig';

/**
 * Currency filters for limiting pool selection in the add liquidity flow
 * Used to pre-filter pools based on specific currency addresses from URL parameters
 */
export type CurrencyFilters = {
  /** First currency address to filter by (optional) */
  currencyA?: `0x${string}` | null;
  /** Second currency address to filter by (optional) */
  currencyB?: `0x${string}` | null;
};

type AddLiquidityStateProviderProps = {
  positionId?: string | null;
  poolId?: string | null;
  /** Optional currency filters to limit available pools to those containing the specified currencies */
  currencyFilters?: CurrencyFilters | null;
  /** Optional position manager address to query for position details */
  positionManagerAddress?: `0x${string}` | null;
  children: JSX.Element;
};

// Uniswap uses 8 significant digits for prices on their UI
const DEFAULT_TO_SIGNIFICANT_DIGITS = 8;

const DEFAULT_TICK_PRICES: { priceLower: string | null; priceUpper: string | null } = {
  priceLower: null,
  priceUpper: null,
};

const DEFAULT_DEPOSIT_AMOUNT_STATE: { amount: string | null; currency: CurrencyField } = {
  amount: null,
  currency: CurrencyField.CURRENCY_0,
};

export type UseAddLiquidityStateReturnType = ReturnType<typeof useAddLiquidityStateContext>;

export const AddLiquidityStateContext = createContext({} as UseAddLiquidityStateReturnType);

/**
 * Core state management hook for liquidity positions
 * @param positionId - Optional ID of an existing position to modify
 * @param poolId - Optional ID of a pool to add liquidity to
 * @param currencyFilters - Optional currency filters to limit pool selection to those containing specified currencies
 * @param positionManagerAddress - Optional position manager address to query for position details
 * @returns State and methods for managing liquidity position
 */
function useAddLiquidityStateContext({
  positionId,
  poolId,
  currencyFilters,
  positionManagerAddress,
}: {
  positionId?: string | null;
  poolId?: string | null;
  currencyFilters?: CurrencyFilters | null;
  positionManagerAddress?: `0x${string}` | null;
}) {
  const chainId = useChainId();
  // if poolId is provided, use the poolId to get the pool details
  // otherwise, use the first pool as the default pool
  const poolSelected = poolId
    ? getPools(chainId).find((pool) => pool.keyBytes32 === poolId)
    : getPools(chainId)[0];

  // State: Asset symbols - defaults to DEFAULT_POOL_SYMBOLS unless position exists
  const [assetSymbolsState, setAssetSymbols] = useState<[string, string]>([
    poolSelected?.tokens[0] ?? '',
    poolSelected?.tokens[1] ?? '',
  ]);

  // State: Price range - manages price input field values
  const [tickPricesState, setTickPricesState] =
    useState<typeof DEFAULT_TICK_PRICES>(DEFAULT_TICK_PRICES);

  // State: Deposit amounts - tracks input field values and independent currency
  const [depositAmountState, setDepositAmountState] = useState<typeof DEFAULT_DEPOSIT_AMOUNT_STATE>(
    DEFAULT_DEPOSIT_AMOUNT_STATE,
  );

  // State: Full range toggle - only available for new positions
  const [isFullRangeState, setIsFullRangeState] = useState(false);
  const toggleIsFullRange = useCallback(() => setIsFullRangeState((prev) => !prev), []);

  // State: Token order - tracks natural order of tokens in pool
  const [token0AsBaseToken, setToken0AsBaseToken] = useState(true);
  // Track which token pair we've set the default for - prevents overriding user selection on step change
  const [defaultSetForTokens, setDefaultSetForTokens] = useState<string | null>(null);

  // txHash
  const [txHash, setTxHash] = useState<`0x${string}` | undefined>();

  // Effect: Reset tick prices when assets change or full range is enabled
  useEffect(() => {
    // Reset tick prices when full range is enabled
    if (isFullRangeState && (tickPricesState.priceLower || tickPricesState.priceUpper)) {
      setTickPricesState(DEFAULT_TICK_PRICES);
    }
  }, [isFullRangeState, tickPricesState.priceLower, tickPricesState.priceUpper]);

  // Effect: Reset tick prices when assets change
  // previously, after modifying the input prices, the tick prices were not reset after changing the currency pair
  useEffect(() => {
    if (!positionId) {
      // Only reset if not editing an existing position
      setTickPricesState(DEFAULT_TICK_PRICES);
    }
  }, [positionId, assetSymbolsState]);

  return {
    positionId,
    /** Currency filters passed from URL parameters to limit pool selection */
    currencyFilters,
    /** Optional position manager address from URL parameters */
    positionManagerAddress,
    assetSymbolsState,
    setAssetSymbols,
    tickPricesState,
    setTickPricesState,
    depositAmountState,
    setDepositAmountState,
    isFullRangeState,
    toggleIsFullRange,
    token0AsBaseToken,
    setToken0AsBaseToken,
    defaultSetForTokens,
    setDefaultSetForTokens,
    txHash,
    setTxHash,
  };
}

/**
 * Hook for derived liquidity position state and handlers
 * Combines context state with calculated values for prices, amounts, and pool details
 * @returns Combined state and handlers for liquidity management
 */
export function useAddLiquidityState() {
  const {
    positionId,
    currencyFilters,
    positionManagerAddress,
    assetSymbolsState,
    setAssetSymbols,
    tickPricesState,
    setTickPricesState,
    depositAmountState,
    setDepositAmountState,
    isFullRangeState,
    toggleIsFullRange,
    token0AsBaseToken,
    setToken0AsBaseToken,
    defaultSetForTokens,
    setDefaultSetForTokens,
    txHash,
    setTxHash,
  } = useContext(AddLiquidityStateContext);
  const chainId = useChainId();
  const { getSettingValue } = useGlobalSettings();

  const addLiquidityTxDeadlineValue =
    getSettingValue(ADD_LIQUIDITY_SETTINGS_KEYS.TX_DEADLINE) ??
    DEFAULT_SETTINGS[ADD_LIQUIDITY_SETTINGS_KEYS.TX_DEADLINE];
  const { txExpireIn: executionDeadlineDeltaSeconds } =
    addLiquidityTxDeadlineValue as TxDeadlineSettingValue;

  // Chain: Get current chain data
  const pools = useMemo(() => getPools(chainId), [chainId]);

  // Data: Load position or pool details
  const positionDetails = usePositionDetails(
    positionId ? BigInt(positionId) : undefined,
    positionManagerAddress ?? undefined,
  );
  const poolDetails = usePoolDetails(assetSymbolsState);
  // Use seconds for timestamps (API expects Unix timestamp in seconds)
  const endDate = useMemo(() => Math.floor(Date.now() / 1000), []);
  // last 14 days in seconds
  const startDate = useMemo(() => endDate - 60 * 60 * 24 * 14, [endDate]);
  const poolPriceHistory = usePoolPriceHistory({
    chainId,
    poolId: poolDetails?.poolId,
    startDate,
    endDate,
    interval: 'daily',
  });

  // Set the default base/quote token based on token preference (e.g., USDC as quote)
  // Only run ONCE per token pair - tracked via context state to persist across component mounts
  // This prevents overriding user's manual quote token selection during step transitions
  const token0Symbol = poolDetails?.positionTokens[0]?.symbol;
  const token1Symbol = poolDetails?.positionTokens[1]?.symbol;
  useEffect(() => {
    // Create a key from current token symbols
    const currentKey = token0Symbol && token1Symbol ? `${token0Symbol}-${token1Symbol}` : null;

    // Only set default if:
    // 1. We have both token symbols AND
    // 2. This token pair hasn't had its default set yet (user hasn't changed tokens)
    if (currentKey && currentKey !== defaultSetForTokens) {
      setDefaultSetForTokens(currentKey);
      setToken0AsBaseToken(!isToken0PreferredQuoteToken(token0Symbol, token1Symbol));
    }
  }, [
    token0Symbol,
    token1Symbol,
    defaultSetForTokens,
    setDefaultSetForTokens,
    setToken0AsBaseToken,
  ]);

  // Calculate the default tick range for liquidity provision based on historical price movement
  const [defaultTickLower, defaultTickUpper] = useMemo(() => {
    // Get the min and max possible ticks for this pool based on its tick spacing
    const minMaxTick = getMaxMinTick(poolDetails.poolKey.tickSpacing);

    // Extract all historical ticks from price data and filter out extreme values
    // This prevents using global min/max ticks which would create inefficient positions
    const ticks = poolPriceHistory.historicalPriceData
      .map((price) => price.tick)
      .filter((tick) => tick !== minMaxTick.minTick && tick !== minMaxTick.maxTick);

    // Handle the case where no historical data is available or all ticks were filtered out
    if (ticks.length === 0) {
      // If no historical data, create a reasonable range around the current price
      // use +/- 10% of the current price
      const tickSpacing = poolDetails.poolKey.tickSpacing;

      const currentTick = poolDetails.poolState.tick;
      // If currentTick is negative, multiplying by 0.9 results in a less negative number (higher value for the tick range),
      // and multiplying by 1.1 results in a more negative number (lower value for the tick range).
      // We need tickLower < tickUpper.
      // So, if currentTick is negative, the 1.1 factor (more negative result) should be used for the lower tick boundary,
      // and the 0.9 factor (less negative result) for the upper tick boundary.
      const baseTickForLowerRange = currentTick >= 0 ? currentTick * 0.9 : currentTick * 1.1;
      const baseTickForUpperRange = currentTick >= 0 ? currentTick * 1.1 : currentTick * 0.9;

      const tickLower = nearestUsableTick(
        Math.max(Math.floor(baseTickForLowerRange), minMaxTick.minTick),
        tickSpacing,
      );
      const tickUpper = nearestUsableTick(
        Math.min(Math.ceil(baseTickForUpperRange), minMaxTick.maxTick),
        tickSpacing,
      );
      return [tickLower, tickUpper];
    }

    // Find the minimum and maximum historical ticks to calculate the price range
    const minHistoricalTick = Math.min(...ticks);
    const maxHistoricalTick = Math.max(...ticks);

    // Get the current tick of the pool to center our calculation
    const currentTick = poolDetails.poolState.tick;

    // Calculate a symmetric range around the current price based on historical volatility
    // maxSpread represents the maximum historical deviation from the current price in either direction
    const maxSpread = Math.max(currentTick - minHistoricalTick, maxHistoricalTick - currentTick);

    // Create a tick range using the calculated spread, adjusting to the nearest valid tick
    // This ensures the position captures the expected price movement based on recent history
    let tickLower = nearestUsableTick(currentTick - maxSpread, poolDetails.poolKey.tickSpacing);
    let tickUpper = nearestUsableTick(currentTick + maxSpread, poolDetails.poolKey.tickSpacing);

    return [tickLower, tickUpper];
  }, [
    poolPriceHistory.historicalPriceData,
    poolDetails.poolKey.tickSpacing,
    poolDetails.poolState.tick,
  ]);

  // Derived: Combine position/pool details
  const {
    feePercent,
    poolId,
    poolKey,
    poolState,
    positionTokens,
    tickUpper,
    tickLower,
    tickAtLimit,
  } = useMemo(() => {
    const detailsWithDefaultTicks = {
      ...poolDetails,
      tickLower: defaultTickLower,
      tickUpper: defaultTickUpper,
      tickAtLimit: {
        [Bound.LOWER]:
          defaultTickLower ===
          nearestUsableTick(TickMath.MIN_TICK, poolDetails.poolKey.tickSpacing),
        [Bound.UPPER]:
          defaultTickUpper ===
          nearestUsableTick(TickMath.MAX_TICK, poolDetails.poolKey.tickSpacing),
      },
    };
    return positionDetails ?? detailsWithDefaultTicks;
  }, [positionDetails, poolDetails, defaultTickLower, defaultTickUpper]);

  // The derived assetSymbols by either positionDetails or assetSymbolsState
  // If the positionId is provide, the assetSymbols are immutable and derived from the positionDetails
  const assetSymbols = useMemo(
    () =>
      positionDetails
        ? [positionDetails.positionTokens[0].symbol, positionDetails.positionTokens[1].symbol]
        : assetSymbolsState,
    [positionDetails, assetSymbolsState],
  );

  // Handlers: Token base/quote switching
  const handleSetToken0AsBaseToken = useCallback(() => {
    setToken0AsBaseToken(true);
    setTickPricesState(DEFAULT_TICK_PRICES);
  }, [setToken0AsBaseToken, setTickPricesState]);

  // Handlers for changing the token0AsBaseToken state
  // This handler will reset the tick prices state to the default
  const handleSetToken1AsBaseToken = useCallback(() => {
    setToken0AsBaseToken(false);
    setTickPricesState(DEFAULT_TICK_PRICES);
  }, [setToken0AsBaseToken, setTickPricesState]);

  // Defining the base and quote token depending on the token0AsBaseToken state
  // The base and quote tokens are used to calculate ticks and prices in the UI
  const { baseToken, quoteToken } = useMemo(
    () => ({
      baseToken: token0AsBaseToken ? positionTokens[0].token : positionTokens[1].token,
      quoteToken: token0AsBaseToken ? positionTokens[1].token : positionTokens[0].token,
    }),
    [token0AsBaseToken, positionTokens],
  );

  const minMaxTick = useMemo(() => getMaxMinTick(poolKey.tickSpacing), [poolKey.tickSpacing]);

  // Defining the tickRange depending on the isFullRange state
  // The tickRange values are used to calculate the deposit amounts in the UI
  // isFullRange is defined by either the position or the isFullRangeState
  const { isFullRange, ...tickRange } = useMemo(() => {
    const rangeLower = isFullRangeState ? minMaxTick.minTick : tickLower;
    const rangeUpper = isFullRangeState ? minMaxTick.maxTick : tickUpper;
    // The position isFullRange if both the tickLower and tickUpper are the min and max ticks
    const positionIsFullRange =
      tickLower === minMaxTick.minTick && tickUpper === minMaxTick.maxTick;
    return {
      lower: token0AsBaseToken ? rangeLower : rangeUpper,
      upper: token0AsBaseToken ? rangeUpper : rangeLower,
      isFullRange: isFullRangeState || positionIsFullRange,
    };
  }, [token0AsBaseToken, tickLower, tickUpper, isFullRangeState, minMaxTick]);

  // Derive the Price objects from the main tickRange
  // The properties are Price<Currency, Currency> and are immutable
  // These derived prices are memoized and used as defaults if the user-input is not set
  const derivedTickPrices = useMemo(
    () => ({
      lower: tickToPrice(baseToken, quoteToken, tickRange.lower),
      upper: tickToPrice(baseToken, quoteToken, tickRange.upper),
      current: tickToPrice(baseToken, quoteToken, poolState.tick),
    }),
    [baseToken, quoteToken, tickRange, poolState.tick],
  );

  // Generate the UI input field values from the user-input if exists, otherwise use the derived tick prices
  // Always use the derived tick prices if positionId is provided
  // The priceInputs values are human readable and are used to display the price input field values
  const priceInputs = useMemo(() => {
    const lowerInitial = derivedTickPrices.lower.toSignificant(DEFAULT_TO_SIGNIFICANT_DIGITS);
    const upperInitial = derivedTickPrices.upper.toSignificant(DEFAULT_TO_SIGNIFICANT_DIGITS);
    const currentInitial = derivedTickPrices.current.toSignificant(DEFAULT_TO_SIGNIFICANT_DIGITS);
    // We keep track of the initial values to help determine if the original derived tick should be used
    // This is useful when the user-input is not set and the derived tick prices are used as defaults
    return {
      lower: {
        initial: lowerInitial,
        value:
          positionId || tickPricesState.priceLower == null
            ? lowerInitial
            : tickPricesState.priceLower,
      },
      upper: {
        initial: upperInitial,
        value:
          positionId || tickPricesState.priceUpper == null
            ? upperInitial
            : tickPricesState.priceUpper,
      },
      current: {
        initial: currentInitial,
        value: currentInitial,
      },
    };
  }, [positionId, tickPricesState, derivedTickPrices]);

  // The priceInputsTicks are used to calculate the deposit amounts in the UI
  // priceInputsTicks are numeric tick indices
  const priceInputsTicks = useMemo(() => {
    // If positionId is provided, use the derived tick prices always
    // If the initial value is the same as the user-input, use the derived tick prices
    const lowerPrice =
      positionId ||
      priceInputs.lower.initial === priceInputs.lower.value ||
      !isPositiveNumericInputValue(priceInputs.lower.value)
        ? derivedTickPrices.lower
        : tryParsePrice(baseToken, quoteToken, priceInputs.lower.value);
    const upperPrice =
      positionId ||
      priceInputs.upper.initial === priceInputs.upper.value ||
      !isPositiveNumericInputValue(priceInputs.upper.value)
        ? derivedTickPrices.upper
        : tryParsePrice(baseToken, quoteToken, priceInputs.upper.value);
    // If the isFullRange state is true or positionId is provided, use the tickRange.lower or tickRange.upper
    // It's unnecessary to calculate the closest tick when isFullRange is true or positionId is provided
    //
    // we need to make sure the nearestUsableTick to account for the tickSpacing, otherwise, useCalculateLiquidity will throw an error
    const lowerTick =
      isFullRange || positionId
        ? tickRange.lower
        : nearestUsableTick(priceToClosestTick(lowerPrice), poolKey.tickSpacing);
    const upperTick =
      isFullRange || positionId
        ? tickRange.upper
        : nearestUsableTick(priceToClosestTick(upperPrice), poolKey.tickSpacing);
    return {
      lower: lowerTick,
      upper: upperTick,
    };
  }, [
    positionId,
    priceInputs.lower.initial,
    priceInputs.lower.value,
    priceInputs.upper.initial,
    priceInputs.upper.value,
    derivedTickPrices.lower,
    derivedTickPrices.upper,
    baseToken,
    quoteToken,
    isFullRange,
    tickRange.lower,
    tickRange.upper,
    poolKey.tickSpacing,
  ]);

  // Determine if the priceInputsTicks generated is out of range
  const tickOutOfRange = useMemo(() => {
    // This needs to be inverted as the calculation is expecting lower to be the lower numeric value of the tick
    const outOfRange = token0AsBaseToken
      ? poolState.tick < priceInputsTicks.lower || poolState.tick > priceInputsTicks.upper
      : poolState.tick < priceInputsTicks.upper || poolState.tick > priceInputsTicks.lower;
    return outOfRange;
  }, [poolState.tick, priceInputsTicks.lower, priceInputsTicks.upper, token0AsBaseToken]);

  // Calculations: Deposit amounts and liquidity
  const calculateLiquidityParams = useMemo(() => {
    const independentTokenDecimals =
      depositAmountState.currency === CurrencyField.CURRENCY_0
        ? positionTokens[0].token.decimals
        : positionTokens[1].token.decimals;
    const independentAmount = convertNumberToBigIntByDecimals(
      depositAmountState.amount ?? '',
      independentTokenDecimals,
    );
    // This needs to be inverted back because calculateLiquidity always uses the token0AsBaseToken state
    const priceTickLower = token0AsBaseToken ? priceInputsTicks.lower : priceInputsTicks.upper;
    const priceTickUpper = token0AsBaseToken ? priceInputsTicks.upper : priceInputsTicks.lower;
    return {
      chainId,
      poolKey,
      poolState,
      tickLower: priceTickLower,
      tickUpper: priceTickUpper,
      independentField: depositAmountState.currency,
      independentAmount,
    };
  }, [
    chainId,
    depositAmountState.amount,
    depositAmountState.currency,
    poolKey,
    poolState,
    positionTokens,
    priceInputsTicks.lower,
    priceInputsTicks.upper,
    token0AsBaseToken,
  ]);

  const { liquidity, dependentFieldAmount } = useCalculateLiquidity(calculateLiquidityParams);
  // Generate the depositAmountsInput and depositAmounts
  // The depositAmountsInput is the input values for the deposit amounts
  // The depositAmounts are bigint values used in the mutation request
  const { depositAmountsInput, depositAmounts } = useMemo(() => {
    const alternateDepositAmount =
      calculateLiquidityParams.independentAmount > 0n ? dependentFieldAmount : 0n;
    return {
      depositAmountsInput: [
        depositAmountState.currency === CurrencyField.CURRENCY_0
          ? depositAmountState.amount
          : isPositiveNumericInputValue(depositAmountState.amount)
          ? parseAssetAmountInMinUnit(dependentFieldAmount, positionTokens[0].asset).toString()
          : null,
        depositAmountState.currency === CurrencyField.CURRENCY_1
          ? depositAmountState.amount
          : isPositiveNumericInputValue(depositAmountState.amount)
          ? parseAssetAmountInMinUnit(dependentFieldAmount, positionTokens[1].asset).toString()
          : null,
      ],
      depositAmounts: [
        depositAmountState.currency === CurrencyField.CURRENCY_0
          ? calculateLiquidityParams.independentAmount
          : alternateDepositAmount,
        depositAmountState.currency === CurrencyField.CURRENCY_1
          ? calculateLiquidityParams.independentAmount
          : alternateDepositAmount,
      ],
    };
  }, [
    depositAmountState.currency,
    depositAmountState.amount,
    dependentFieldAmount,
    positionTokens,
    calculateLiquidityParams.independentAmount,
  ]);

  // Asynchronously get the account balance with an array of token addresses
  // We memoize the token addresses to avoid unnecessary multiple requests on useAccountBalances
  const positionTokenAddresses = useMemo(
    () => positionTokens.map((token) => token.asset),
    [positionTokens],
  );
  const accountBalances = useAccountBalances(positionTokenAddresses);

  // for out-of-range positions, disable the one of the deposit inputs
  // assuming input0 is always for poolKey.currency0 and input1 is always for poolKey.currency1
  const [isCurrency0DepositDisabled, isCurrency1DepositDisabled] = useMemo(() => {
    // This needs to be inverted back because calculateLiquidity always uses the token0AsBaseToken state
    const priceTickLower = token0AsBaseToken ? priceInputsTicks.lower : priceInputsTicks.upper;
    const priceTickUpper = token0AsBaseToken ? priceInputsTicks.upper : priceInputsTicks.lower;

    return [
      Boolean(priceInputsTicks.upper && poolState && poolState.tick >= priceTickUpper),
      Boolean(priceInputsTicks.lower && poolState && poolState.tick <= priceTickLower),
    ];
  }, [token0AsBaseToken, priceInputsTicks.lower, priceInputsTicks.upper, poolState]);

  // get the latest position id from the txHash
  // note that this only applies to the minting of a new position
  // for increasing liquidity, the value will be null
  const latestPositionId = useLatestPositionId(txHash);

  // Derive positionManagerAddress from the pool's hook address
  const derivedPositionManagerAddress = useMemo(() => {
    return getPositionManagerForHook(poolKey.hooks, chainId);
  }, [poolKey.hooks, chainId]);

  // Return: Combined state and handlers
  return {
    positionId,
    /** Currency filters for limiting available pool selection based on URL parameters */
    currencyFilters,
    baseToken,
    quoteToken,
    assetSymbols,
    setAssetSymbols,
    tickPricesState,
    setTickPricesState,
    priceInputs,
    depositAmounts,
    depositAmountState,
    depositAmountsInput,
    setDepositAmountState,
    liquidity,
    isFullRange,
    toggleIsFullRange,
    token0AsBaseToken,
    handleSetToken0AsBaseToken,
    handleSetToken1AsBaseToken,
    feePercent,
    pools,
    poolId,
    poolKey,
    poolState,
    positionTokens,
    priceInputsTicks,
    // tickAtLimit is only retrieved from the positionDetails (existing position)
    // when adding a new position, and isFullRange is true,
    // we need to manually set the tickAtLimit
    tickAtLimit: isFullRange
      ? {
          [Bound.LOWER]: minMaxTick.minTick,
          [Bound.UPPER]: minMaxTick.maxTick,
        }
      : tickAtLimit,
    tickOutOfRange,
    tickInRange: !tickOutOfRange,
    accountBalances,
    currencyDepositDisabled: {
      [CurrencyField.CURRENCY_0]: isCurrency0DepositDisabled,
      [CurrencyField.CURRENCY_1]: isCurrency1DepositDisabled,
    },
    txHash,
    setTxHash,
    latestPositionId,
    executionDeadlineDeltaSeconds,
    /** Position manager address derived from the pool's hook */
    positionManagerAddress: derivedPositionManagerAddress,
  };
}

/**
 * Context provider for liquidity position management
 * @param props.positionId - Optional ID of position to modify
 * @param props.poolId - Optional ID of pool to add liquidity to
 * @param props.currencyFilters - Optional currency filters to limit pool selection
 * @param props.positionManagerAddress - Optional position manager address for position details
 * @param props.children - Child components to wrap
 */
export const AddLiquidityStateProvider = memo(function AddLiquidityStateProvider({
  positionId,
  poolId,
  currencyFilters,
  positionManagerAddress,
  children,
}: AddLiquidityStateProviderProps) {
  const context = useAddLiquidityStateContext({
    positionId,
    poolId,
    currencyFilters,
    positionManagerAddress,
  });
  return (
    <AddLiquidityStateContext.Provider value={context}>
      {children}
    </AddLiquidityStateContext.Provider>
  );
});
